/**
 * Payload field management — DOM helpers for the hidden gfsh_payload field.
 *
 * Every helper operates on all copies of the form. A page that renders the same
 * form twice, showing one copy per breakpoint, leaves two elements with
 * `id="gform_<id>"` on the page, and either could be the one submitted.
 *
 * @module collector/payload
 */

import type { PoWManager, PoWSolution } from './pow-manager';
import { createLogger } from '@shared/logger';

const logger = createLogger('Collector');

/**
 * Returns every copy of a form on the page.
 *
 * Attribute selector rather than `#gform_<id>`, so it cannot be misread as a
 * single-ID lookup.
 */
export function getFormCopies(formId: number): HTMLElement[] {
	return Array.from(
		document.querySelectorAll<HTMLElement>(`form[id="gform_${formId}"]`)
	);
}

/**
 * Ensures the hidden payload field exists in every copy of the form.
 *
 * Creates it where it doesn't exist (e.g., if the form was loaded via AJAX).
 */
export function ensurePayloadField(formId: number, fieldName: string): void {
	const forms = getFormCopies(formId);

	if (!forms.length) {
		logger.warn(
			`formId ${formId} — form element #gform_${formId} not found in DOM`
		);
		return;
	}

	for (const form of forms) {
		if (form.querySelector(`input[name="${fieldName}"]`)) {
			continue;
		}

		const input = document.createElement('input');
		input.type = 'hidden';
		input.name = fieldName;
		input.value = '';
		form.appendChild(input);
	}

	logger.log(
		`formId ${formId} — payload field "${fieldName}" present in ${forms.length} form cop${forms.length === 1 ? 'y' : 'ies'}`
	);
}

/**
 * Writes a PoW solution to the hidden payload field of every copy of a form.
 *
 * The same solution goes into each: only one copy is ever submitted, so the
 * challenge nonce is still consumed exactly once.
 */
export function writePayloadField(
	formId: number,
	solution: PoWSolution,
	fieldName: string
): void {
	const fields = getFormCopies(formId)
		.map((form) =>
			form.querySelector<HTMLInputElement>(`input[name="${fieldName}"]`)
		)
		.filter((field): field is HTMLInputElement => field !== null);

	if (!fields.length) {
		logger.warn(
			`formId ${formId} — payload field not found: form[id="gform_${formId}"] input[name="${fieldName}"]`
		);
		return;
	}

	const encoded = btoa(JSON.stringify(assemblePayload(solution)));

	for (const field of fields) {
		field.value = encoded;
	}

	logger.log(
		`formId ${formId} — payload written to ${fields.length} field(s)`
	);
}

/**
 * Assembles the collector payload from a PoW solution.
 *
 * @return The payload object to be JSON-encoded and base64'd.
 */
export function assemblePayload(
	solution: NonNullable<ReturnType<PoWManager['getSolution']>>
): Record<string, unknown> {
	return {
		v: 1, // Payload version for future compatibility.
		t: Date.now(), // Client timestamp.
		pow: solution,
	};
}
